import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Checkbox,
    TextField,
    Button,
    Box,
    LinearProgress,
    Slider,
    Rating,
    Select,
    MenuItem,
    FormGroup,
    Alert,
    Link
} from '@mui/material';

const Questionnaire = ({
    title = "Ankieta",
    description = "",
    questions = [],
    onComplete,
    onBack,
    showProgress = true,
    submitButtonText = "Zakończ ankietę"
}) => {
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [answers, setAnswers] = useState({});
    const [otherTexts, setOtherTexts] = useState({}); // Store text for "other" options

    // Effect to ensure we start on a visible question
    useEffect(() => {
        const shouldShowQuestionFunc = (question) => {
            if (!question.conditional) return true;

            const { dependsOn, showWhen, hideWhen } = question.conditional;
            const dependentAnswer = answers[dependsOn];

            if (showWhen) {
                return dependentAnswer === showWhen;
            }

            if (hideWhen) {
                if (Array.isArray(dependentAnswer)) {
                    return !dependentAnswer.includes(hideWhen);
                }
                return dependentAnswer !== hideWhen;
            }

            return true;
        };

        if (questions.length > 0 && !shouldShowQuestionFunc(questions[currentQuestion])) {
            const firstVisibleQuestion = questions.findIndex(q => shouldShowQuestionFunc(q));
            if (firstVisibleQuestion !== -1) {
                setCurrentQuestion(firstVisibleQuestion);
            }
        }
    }, [answers, currentQuestion, questions]);

    const handleAnswer = (questionId, answer) => {
        setAnswers(prev => ({
            ...prev,
            [questionId]: answer
        }));
    };

    const findNextVisibleQuestion = (startIndex) => {
        for (let i = startIndex + 1; i < questions.length; i++) {
            if (shouldShowQuestion(questions[i])) {
                return i;
            }
        }
        return -1; // No more visible questions
    };

    const findPreviousVisibleQuestion = (startIndex) => {
        for (let i = startIndex - 1; i >= 0; i--) {
            if (shouldShowQuestion(questions[i])) {
                return i;
            }
        }
        return -1; // No previous visible questions
    };

    const handleNext = () => {
        const nextQuestion = findNextVisibleQuestion(currentQuestion);
        if (nextQuestion !== -1) {
            setCurrentQuestion(nextQuestion);
        } else {
            // Complete questionnaire - merge "other" texts with answers
            const finalAnswers = { ...answers };
            Object.keys(otherTexts).forEach(questionId => {
                if (otherTexts[questionId]) {
                    finalAnswers[`${questionId}_other_text`] = otherTexts[questionId];
                }
            });

            if (onComplete) {
                onComplete(finalAnswers);
            }
        }
    };

    const handlePrevious = () => {
        const prevQuestion = findPreviousVisibleQuestion(currentQuestion);
        if (prevQuestion !== -1) {
            setCurrentQuestion(prevQuestion);
        } else if (onBack) {
            onBack();
        }
    };

    const isCurrentQuestionAnswered = () => {
        const question = questions[currentQuestion];
        if (!question) return false;

        const answer = answers[question.id];

        if (question.required === false) return true;

        switch (question.type) {
            case 'multiselect':
                return answer && answer.length > 0;
            case 'text':
            case 'textarea':
                return answer && answer.trim().length > 0;
            case 'consent':
                return answer === true;
            case 'slider':
                return true
            case 'rating':
            case 'select':
            case 'radio':
                return answer !== undefined && answer !== null;
            default:
                return answer !== undefined && answer !== null;
        }
    };

    const shouldShowQuestion = (question) => {
        if (!question.conditional) return true;

        const { dependsOn, showWhen, hideWhen } = question.conditional;
        const dependentAnswer = answers[dependsOn];

        if (showWhen) {
            return dependentAnswer === showWhen;
        }

        if (hideWhen) {
            if (Array.isArray(dependentAnswer)) {
                return !dependentAnswer.includes(hideWhen);
            }
            return dependentAnswer !== hideWhen;
        }

        return true;
    };

    const renderQuestion = (question) => {
        const answer = answers[question.id];

        switch (question.type) {
            case 'radio':
                return (
                    <FormControl component="fieldset" fullWidth>
                        <FormLabel component="legend" sx={{ mb: 2, fontWeight: 'bold' }}>
                            {question.question}
                            {question.required !== false && <span style={{ color: 'red' }}> *</span>}
                        </FormLabel>
                        {question.description && (
                            <Typography variant="body2" sx={{ mb: 2, color: 'grey.600' }}>
                                {question.description}
                            </Typography>
                        )}
                        <RadioGroup
                            value={answer || ''}
                            onChange={(e) => handleAnswer(question.id, e.target.value)}
                        >
                            {question.options.map((option) => (
                                <FormControlLabel
                                    key={option.value}
                                    value={option.value}
                                    control={<Radio />}
                                    label={option.label}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                );

            case 'multiselect':
                const currentAnswers = answer || [];
                const maxSelections = question.maxSelections;
                const hasOtherOption = question.options.some(opt => opt.value === 'other' || opt.value.includes('other'));
                const otherValue = question.options.find(opt => opt.value === 'other' || opt.value.includes('other'))?.value;
                const otherSelected = otherValue && currentAnswers.includes(otherValue);

                return (
                    <FormControl component="fieldset" fullWidth>
                        <FormLabel component="legend" sx={{ mb: 2, fontWeight: 'bold' }}>
                            {question.question}
                            {question.required !== false && <span style={{ color: 'red' }}> *</span>}
                        </FormLabel>
                        {question.description && (
                            <Typography variant="body2" sx={{ mb: 2, color: 'grey.600' }}>
                                {question.description}
                            </Typography>
                        )}
                        {maxSelections && currentAnswers.length >= maxSelections && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Możesz wybrać maksymalnie {maxSelections} opcji.
                            </Alert>
                        )}

                        <FormGroup>
                            {question.options.map((option) => {
                                const isChecked = currentAnswers.includes(option.value);
                                const isDisabled = maxSelections && currentAnswers.length >= maxSelections && !isChecked;

                                return (
                                    <FormControlLabel
                                        key={option.value}
                                        control={
                                            <Checkbox
                                                checked={isChecked}
                                                disabled={isDisabled}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        handleAnswer(question.id, [...currentAnswers, option.value]);
                                                    } else {
                                                        handleAnswer(question.id, currentAnswers.filter(a => a !== option.value));
                                                        // Clear "other" text if unchecking "other" option
                                                        if (option.value === otherValue) {
                                                            setOtherTexts(prev => ({
                                                                ...prev,
                                                                [question.id]: ''
                                                            }));
                                                        }
                                                    }
                                                }}
                                            />
                                        }
                                        label={option.label}
                                    />
                                );
                            })}
                        </FormGroup>

                        {/* Show text field for "other" option when selected - UNDER the entire list */}
                        {hasOtherOption && otherSelected && (
                            <Box sx={{ mt: 2 }}>
                                <TextField
                                    fullWidth
                                    size="medium"
                                    placeholder="Proszę określić..."
                                    value={otherTexts[question.id] || ''}
                                    onChange={(e) => {
                                        setOtherTexts(prev => ({
                                            ...prev,
                                            [question.id]: e.target.value
                                        }));
                                    }}
                                    variant="outlined"
                                    label="Twoja odpowiedź"
                                />
                            </Box>
                        )}
                    </FormControl>
                );

            case 'select':
                return (
                    <FormControl fullWidth>
                        <FormLabel sx={{ mb: 2, fontWeight: 'bold' }}>
                            {question.question}
                            {question.required !== false && <span style={{ color: 'red' }}> *</span>}
                        </FormLabel>
                        {question.description && (
                            <Typography variant="body2" sx={{ mb: 2, color: 'grey.600' }}>
                                {question.description}
                            </Typography>
                        )}
                        <Select
                            value={answer || ''}
                            onChange={(e) => handleAnswer(question.id, e.target.value)}
                            displayEmpty
                        >
                            <MenuItem value="" disabled>
                                Wybierz opcję...
                            </MenuItem>
                            {question.options.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                );

            case 'text':
                return (
                    <Box>
                        <FormLabel sx={{ mb: 2, fontWeight: 'bold', display: 'block' }}>
                            {question.question}
                            {question.required !== false && <span style={{ color: 'red' }}> *</span>}
                        </FormLabel>
                        {question.description && (
                            <Typography variant="body2" sx={{ mb: 2, color: 'grey.600' }}>
                                {question.description}
                            </Typography>
                        )}
                        <TextField
                            fullWidth
                            value={answer || ''}
                            onChange={(e) => handleAnswer(question.id, e.target.value)}
                            placeholder={question.placeholder}
                        />
                    </Box>
                );

            case 'textarea':
                return (
                    <Box>
                        <FormLabel sx={{ mb: 2, fontWeight: 'bold', display: 'block' }}>
                            {question.question}
                            {question.required !== false && <span style={{ color: 'red' }}> *</span>}
                        </FormLabel>
                        {question.description && (
                            <Typography variant="body2" sx={{ mb: 2, color: 'grey.600' }}>
                                {question.description}
                            </Typography>
                        )}
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={answer || ''}
                            onChange={(e) => handleAnswer(question.id, e.target.value)}
                            placeholder={question.placeholder}
                        />
                    </Box>
                );

            case 'rating':
                return (
                    <Box>
                        <FormLabel sx={{ mb: 2, fontWeight: 'bold', display: 'block' }}>
                            {question.question}
                            {question.required !== false && <span style={{ color: 'red' }}> *</span>}
                        </FormLabel>
                        {question.description && (
                            <Typography variant="body2" sx={{ mb: 2, color: 'grey.600' }}>
                                {question.description}
                            </Typography>
                        )}
                        <Rating
                            value={answer || 0}
                            onChange={(event, newValue) => handleAnswer(question.id, newValue)}
                            max={question.max || 5}
                            size="large"
                        />
                    </Box>
                );

            case 'slider':
                return (
                    <Box>
                        <FormLabel sx={{ mb: 2, fontWeight: 'bold', display: 'block' }}>
                            {question.question}
                            {question.required !== false && <span style={{ color: 'red' }}> *</span>}
                        </FormLabel>
                        {question.description && (
                            <Typography variant="body2" sx={{ mb: 2, color: 'grey.600' }}>
                                {question.description}
                            </Typography>
                        )}
                        <Box sx={{ px: 2 }}>
                            <Slider
                                value={answer || question.min || 0}
                                onChange={(event, newValue) => handleAnswer(question.id, newValue)}
                                min={question.min || 0}
                                max={question.max || 100}
                                step={question.step || 1}
                                marks={question.marks}
                                valueLabelDisplay="on"
                            />
                        </Box>
                    </Box>
                );

            case 'rating_multiple':
                return (
                    <Box>
                        <FormLabel sx={{ mb: 2, fontWeight: 'bold', display: 'block' }}>
                            {question.question}
                            {question.required !== false && <span style={{ color: 'red' }}> *</span>}
                        </FormLabel>
                        {question.description && (
                            <Typography variant="body2" sx={{ mb: 3, color: 'grey.600' }}>
                                {question.description}
                            </Typography>
                        )}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {question.options.map((option) => (
                                <Box key={option.value} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                        {option.label}
                                    </Typography>
                                    <Rating
                                        value={answer?.[option.value] || 0}
                                        onChange={(event, newValue) => {
                                            const newAnswer = { ...answer, [option.value]: newValue };
                                            handleAnswer(question.id, newAnswer);
                                        }}
                                        max={5}
                                        size="small"
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Box>
                );

            case 'consent':
                return (
                    <Box>
                        <FormLabel sx={{ mb: 2, fontWeight: 'bold', display: 'block' }}>
                            {question.question}
                            {question.required !== false && <span style={{ color: 'red' }}> *</span>}
                        </FormLabel>
                        {question.description && (
                            <Typography variant="body2" sx={{ mb: 2, color: 'grey.600' }}>
                                {question.description}
                            </Typography>
                        )}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={answer || false}
                                    onChange={(e) => handleAnswer(question.id, e.target.checked)}
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="body2" sx={{ color: 'grey.700' }}>
                                    {question.consentText && (
                                        <>
                                            {question.links && question.links.length > 0 ? (
                                                <>
                                                    {question.consentText.split(question.links[0].text)[0]}
                                                    {question.links.map((link, index) => (
                                                        <React.Fragment key={index}>
                                                            <Link
                                                                href={link.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                sx={{ color: 'primary.main', textDecoration: 'underline' }}
                                                            >
                                                                {link.text}
                                                            </Link>
                                                            {index < question.links.length - 1 && ' oraz '}
                                                        </React.Fragment>
                                                    ))}
                                                    {question.required !== false && ' *'}
                                                </>
                                            ) : (
                                                <>
                                                    {question.consentText}
                                                    {question.required !== false && ' *'}
                                                </>
                                            )}
                                        </>
                                    )}
                                </Typography>
                            }
                            sx={{ alignItems: 'flex-start' }}
                        />
                    </Box>
                );

            default:
                return (
                    <Typography color="error">
                        Nieobsługiwany typ pytania: {question.type}
                    </Typography>
                );
        }
    };

    if (!questions || questions.length === 0) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h5" color="error">
                        Brak pytań do wyświetlenia
                    </Typography>
                </Paper>
            </Container>
        );
    }

    const currentQuestionData = questions[currentQuestion];

    // Calculate progress based on visible questions
    const visibleQuestions = questions.filter(q => shouldShowQuestion(q));
    const currentQuestionIndex = visibleQuestions.findIndex(q => q.id === currentQuestionData?.id);
    const progress = visibleQuestions.length > 0 ? ((currentQuestionIndex + 1) / visibleQuestions.length) * 100 : 0;

    return (
        <Box
            sx={{
                minHeight: '100vh',
                backgroundImage: 'url(/assets/Hero.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
                py: 4
            }}
        >
            <Container maxWidth="md">
                <Paper elevation={3} sx={{ p: 4, backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
                    {/* Header */}
                    <Box sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                            <img src="/logo-192x192.png" alt="Find solution Logo" style={{ height: '32px', width: '32px', marginRight: '12px' }} />
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 'bold',
                                    color: 'primary.main',
                                    textAlign: 'center'
                                }}
                            >
                                Find solution
                            </Typography>
                        </Box>
                        <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ color: 'grey.800' }}>
                            {title}
                        </Typography>
                        {description && (
                            <Typography variant="body1" align="center" sx={{ color: 'grey.600', mb: 3 }}>
                                {description}
                            </Typography>
                        )}

                        {showProgress && (
                            <Box sx={{ mb: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {Math.round(progress)}%
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={progress}
                                    sx={{
                                        height: 8,
                                        borderRadius: 4,
                                        bgcolor: 'grey.200',
                                        '& .MuiLinearProgress-bar': {
                                            background: 'linear-gradient(90deg, #c79a5f, #9b7b5e)',
                                            borderRadius: 4
                                        }
                                    }}
                                />
                            </Box>
                        )}
                    </Box>

                    {/* Question */}
                    <Box sx={{ mb: 4, minHeight: '200px' }}>
                        {shouldShowQuestion(currentQuestionData) ? renderQuestion(currentQuestionData) : (
                            <Typography variant="body1" sx={{ textAlign: 'center', color: 'grey.500', mt: 8 }}>
                                To pytanie zostało pominięte na podstawie Twoich wcześniejszych odpowiedzi.
                            </Typography>
                        )}
                    </Box>

                    {/* Navigation */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Button
                            variant="outlined"
                            onClick={handlePrevious}
                            disabled={currentQuestion === 0 && !onBack}
                        >
                            {currentQuestion === 0 ? 'Wstecz' : 'Poprzednie'}
                        </Button>


                        <Button
                            variant="contained"
                            onClick={handleNext}
                            disabled={!isCurrentQuestionAnswered()}
                            sx={{
                                background: isCurrentQuestionAnswered()
                                    ? 'linear-gradient(90deg, #c79a5f, #9b7b5e)'
                                    : undefined,
                                '&:hover': isCurrentQuestionAnswered() ? {
                                    background: 'linear-gradient(90deg, #c79a5f, #9b7b5e)',
                                    transform: 'scale(1.05)'
                                } : undefined
                            }}
                        >
                            {currentQuestion === questions.length - 1 ? submitButtonText : 'Dalej'}
                        </Button>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default Questionnaire;
